/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../NavLinks';

import ClubRegistrationConstants from '../utils/ClubRegistrationConstants';
import ClubConstants from '../../club/utils/ClubConstants';

import useNewClubRegistrationData from '../../../state/clubRegistrationNew/forPromiseCalls/UseNewClubRegistrationData';
import useClubRegistrationNavData from '../../../state/clubRegistration/leftNavigation/UseClubRegistrationNavData';
import useUsasClubStaffData from '../../../state/clubStaff/UseUsasClubStaffData';
import useRequiredRolesData from '../../../state/clubRegistration/requiredRoles/UseRequiredRolesData';
import useOrgLevelTypeHelpTextData from '../../../state/clubRegistration/orgLevelTypeHelpText/UseOrgLevelTypeHelpTextData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

const registrationRoles = [
    ClubConstants.COACH_ROLE_NAME,
    ClubConstants.JUNIOR_COACH_ROLE_NAME,
    ClubConstants.PROVISIONAL_COACH_ROLE_NAME
];

const useNewClubRegistrationStaff = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [state, setState] = useState(getInitialState());
    const [submitPrequisitesState, setSubmitPrequisitesState] = useState({ areSubmitPrerequisitesDone: false });

    const { clubRegistrationState } = useNewClubRegistrationData();
    const { clubRegistrationNavState, setClubRegistrationNavState } = useClubRegistrationNavData();
    const { requiredRolesState, getRequiredRolesByOrgLevelTypeId, getRequiredRolesByOrgLevelTypeIdAndOrgUnitId } = useRequiredRolesData();

    const { usasClubStaffState, getUsasClubStaff, deleteUsasClubStaff } = useUsasClubStaffData();
    const { orgLevelTypeHelpTextState, getOrgLevelTypeHelpText } = useOrgLevelTypeHelpTextData();

    const [modalState, setModalState] = useState(getInitialModalState());
    const [requiredRolesFormattedState, setRequiredRolesFormattedState] = useState({ isArrayLoaded: false, arrayData: [] });

    const { environmentVariableState, HEAD_COACH_ORG_ROLE_ID, CLUB_STAFF_ROLE_GROUP_ID, CLUB_BOARD_MEMBER_ORG_ROLE_ID } = useEnvironmentVariableData();

    const validateStaff = () => {
        let requiredRolesArray = requiredRolesFormattedState.arrayData;
        let roleMet = false;

        for (const roleIdx in requiredRolesArray) {
            roleMet = requiredRolesArray[roleIdx].met;
            if (roleMet === false) { break; }
        }

        return roleMet;
    }

    const onEdit = (e, orgRoleId, orgRoleName) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        };

        navigate(NavLinks.CLUB_REGISTRATION_STAFF_DETAIL, { state: { orgRoleId, orgRoleName } });
    }

    const onDelete = (e, personOrgRoleDurationId) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        };

        deleteUsasClubStaff(personOrgRoleDurationId);
        setState({ ...state, tryGetAfterDelete: true });
    };

    const onContinueClicked = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        setState({ ...state, tryRedirect: true, submitButton: ClubRegistrationConstants.CONTINUE });
    }

    const onSaveAndExitClicked = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        setState({ ...state, tryRedirect: true, submitButton: ClubRegistrationConstants.EXIT });
    }

    const onBackClicked = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        setState({ ...state, tryRedirect: true, submitButton: ClubRegistrationConstants.BACK });
    }

    const onOpenModal = () => {
        setModalState({ ...modalState, modalTitle: ClubRegistrationConstants.EXIT_CONFIRMATION, displayPopUp: true });
    }


    const updateNavState = (isComplete) => {
        for (let i = 0; i < clubRegistrationNavState.length; i++) {
            clubRegistrationNavState[i].isCurrent = false
        }
        const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
        if (!isComplete) {
            clubRegistrationNavState[navIndex] =
            {
                ...clubRegistrationNavState[navIndex],
                isCurrent: true,
                isVisited: true,
                arePrerequisitesDone: true
            }
        }
        else if (isComplete === true) {
            clubRegistrationNavState[navIndex] =
            {
                ...clubRegistrationNavState[navIndex],
                isCurrent: true,
                isComplete: true,
                isVisited: true,
                arePrerequisitesDone: true
            }
        }
        else if (isComplete === false) {
            clubRegistrationNavState[navIndex] =
            {
                ...clubRegistrationNavState[navIndex],
                isCurrent: true,
                isComplete: false,
                isVisited: true,
                arePrerequisitesDone: true
            }
        }

        //Submit
        let areSubmitPrerequisitesDone = true;
        let isSubmitComplete = false;
        for (let i = 0; i < clubRegistrationNavState.length; i++) {
            if (clubRegistrationNavState[i].name !== ClubRegistrationConstants.SUBMIT
                && clubRegistrationNavState[i].isComplete === false) {
                areSubmitPrerequisitesDone = false;
            }
            if (clubRegistrationNavState[i].name === ClubRegistrationConstants.SUBMIT) {
                if (clubRegistrationState.objData.status === ClubRegistrationConstants.PENDING_STATUS ||
                    clubRegistrationState.objData.status === ClubRegistrationConstants.APPROVED_STATUS) {
                    isSubmitComplete = true
                }
            }
        }
        const submitNavIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.SUBMIT)
        clubRegistrationNavState[submitNavIndex] =
        {
            ...clubRegistrationNavState[submitNavIndex],
            isCurrent: false,
            isComplete: isSubmitComplete,
            isVisited: areSubmitPrerequisitesDone === false ? false : clubRegistrationNavState[submitNavIndex].isVisited,
            arePrerequisitesDone: areSubmitPrerequisitesDone
        }
        setSubmitPrequisitesState({ ...state, areSubmitPrerequisitesDone: areSubmitPrerequisitesDone });
        setClubRegistrationNavState(clubRegistrationNavState);
    }

    useEffect(() => {
        const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
        if (clubRegistrationNavState[navIndex].isCurrent === false) {
            updateNavState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubRegistrationNavState])

    useEffect(() => {
        const submitNavIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.SUBMIT)
        if (clubRegistrationNavState[submitNavIndex].arePrerequisitesDone === false
            && submitPrequisitesState.areSubmitPrerequisitesDone === true
        ) {
            updateNavState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubRegistrationNavState, submitPrequisitesState.areSubmitPrerequisitesDone])

    useEffect(() => {
        //Handle inital page load
        if (Object.keys(clubRegistrationState.objData).length > 0 &&
            clubRegistrationState.isObjLoading === false &&
            clubRegistrationState.isObjLoaded === true &&
            state.isIntialPageLoadComplete === false &&
            environmentVariableState.isLoaded === true) {
            //Get any staff associated with the club
            if ((usasClubStaffState.isArrayLoading === false) || (location.state && location.state.addedStaff)) {
                getUsasClubStaff(clubRegistrationState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
            }
            if (usasClubStaffState.isArrayLoaded === true) {
                setState({ ...state, isIntialPageLoadComplete: true });
            }

            //Get the help text for the org level type
            const getOrgLevelTypeHelpTextPromise = getOrgLevelTypeHelpText(clubRegistrationState.objData.clubOwnerType.orgLevelTypeId);

            if (getOrgLevelTypeHelpTextPromise !== null) {
                getOrgLevelTypeHelpTextPromise.then((newState) => {
                    if (newState !== null) {
                        //nothing to do here
                    }
                }).catch((e) => {
                    //TODO state error
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environmentVariableState, clubRegistrationState.objData,
        usasClubStaffState.arrayData,
        state.isIntialPageLoadComplete]);

    useEffect(() => {
        //If allowed to redirect, then navigate to the correct page
        if (state.submitButton !== '' && clubRegistrationState.isObjLoaded === true && usasClubStaffState.isArrayLoaded === true) {
            if (state.tryRedirect === false) {
                onOpenModal();
            }
            if (state.tryRedirect === true) {
                if (validateStaff() === true) {
                    const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
                    clubRegistrationNavState[navIndex] =
                    {
                        ...clubRegistrationNavState[navIndex],
                        isCurrent: false,
                        isComplete: true,
                        isVisited: true,
                        arePrerequisitesDone: true
                    }
                    setClubRegistrationNavState(clubRegistrationNavState);
                }
                else {
                    const navIndex = clubRegistrationNavState.findIndex(x => x.name === ClubRegistrationConstants.CLUB_STAFF)
                    clubRegistrationNavState[navIndex] =
                    {
                        ...clubRegistrationNavState[navIndex],
                        isCurrent: false,
                        isComplete: false,
                        isVisited: true,
                        arePrerequisitesDone: true
                    }
                    setClubRegistrationNavState(clubRegistrationNavState);
                }

                if (state.submitButton === ClubRegistrationConstants.CONTINUE) {
                    navigate(NavLinks.CLUB_REGISTRATION_LEGAL_ENTITY);
                }
                else if (state.submitButton === ClubRegistrationConstants.EXIT) {
                    navigate(NavLinks.CLUB_REGISTRATION_ENTRY_NEW);
                }
                else if (state.submitButton === ClubRegistrationConstants.BACK) {
                    navigate(NavLinks.CLUB_REGISTRATION_INFO);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, clubRegistrationState.isObjLoaded])


    const findNameByPersonOrgRoleDurationId = (staff, personOrgRoleDurationId) => {
        const staffMember = staff.find(member =>
            member.personOrgRole.some(role => role.personOrgRoleDurationId === personOrgRoleDurationId)
        );

        return staffMember.firstName + ' ' + staffMember.lastName
    };

    useEffect(() => {
        if (usasClubStaffState.isArrayLoaded === true && requiredRolesState.isArrayLoaded === true) {
            const staff = usasClubStaffState.arrayData;
            let boardMemberStaffArray = [];

            staff.forEach(staffMember => {
                staffMember.personOrgRole.forEach(role => {
                    if (role.orgRoleId === CLUB_BOARD_MEMBER_ORG_ROLE_ID) {
                        let boardMemberStaffObj = {};
                        boardMemberStaffObj.name = staffMember.firstName + ' ' + staffMember.lastName;
                        boardMemberStaffObj.orgRoleId = role.orgRoleId;
                        boardMemberStaffObj.personOrgRoleDurationId = role.personOrgRoleDurationId;

                        boardMemberStaffArray.push(boardMemberStaffObj);
                    }
                });
            });

            let staffRolesSimple = [];
            let requiredRolesArray = [];

            for (const staffIdx in staff) {
                const staffRole = staff[staffIdx].personOrgRole;
                for (const roleIdx in staffRole) {
                    staffRolesSimple.push(staffRole[roleIdx].orgRoleId);
                }
            }

            let count = 0;
            for (const roleIdx in requiredRolesState.arrayData) {
                //is a staff member assigned with a role in the required list, if so, set met to true
                let found = staffRolesSimple.some(roleId => roleId === requiredRolesState.arrayData[roleIdx].requiredRoleId);
                let foundChildrenRole = false;
                if (found === false && requiredRolesState.arrayData[roleIdx].childrenRole) {
                    for (const childRoleIdx in requiredRolesState.arrayData[roleIdx].childrenRole) {
                        foundChildrenRole = staffRolesSimple.some(roleId => roleId === requiredRolesState.arrayData[roleIdx].childrenRole[childRoleIdx].orgRoleId);

                        if (foundChildrenRole) { break; }
                    }
                }

                let personOrgRoleDurationId = -1;
                let name = "";
                try {
                    personOrgRoleDurationId = staff.find(x => x.personOrgRole.find(y => y.orgRoleId === requiredRolesState.arrayData[roleIdx].requiredRoleId)).personOrgRole.find(y => y.orgRoleId === requiredRolesState.arrayData[roleIdx].requiredRoleId).personOrgRoleDurationId;
                    name = findNameByPersonOrgRoleDurationId(staff, personOrgRoleDurationId);
                }
                catch (e) {
                    personOrgRoleDurationId = -1;
                }

                let val = requiredRolesArray.find(x => x.name === name && x.orgRoleId === requiredRolesState.arrayData[roleIdx].requiredRoleId);

                if (val && val.met === true) {
                    found = false;
                    name = "";
                    personOrgRoleDurationId = -1;
                }

                if (requiredRolesState.arrayData[roleIdx].requiredRoleId !== CLUB_BOARD_MEMBER_ORG_ROLE_ID) {
                    requiredRolesArray.push({
                        roleNum: count++,
                        orgRoleId: requiredRolesState.arrayData[roleIdx].requiredRoleId,
                        orgRoleName: requiredRolesState.arrayData[roleIdx].requiredRoleName,
                        personOrgRoleDurationId: personOrgRoleDurationId || -1,
                        name: name,
                        met: found || foundChildrenRole
                    });
                }
            }

            let boardSlots = requiredRolesState.arrayData.filter(x => x.requiredRoleId === CLUB_BOARD_MEMBER_ORG_ROLE_ID);
            let boardSlotCnt = 0;
            boardSlots.forEach(boardSlot => {
                let name = "";
                let found = false;
                let personOrgRoleDurationId = -1;

                if (boardMemberStaffArray.length > boardSlotCnt) {
                    if (boardMemberStaffArray[boardSlotCnt].name.length > 0) {
                        name = boardMemberStaffArray[boardSlotCnt].name;
                        found = true;
                        personOrgRoleDurationId = boardMemberStaffArray[boardSlotCnt].personOrgRoleDurationId;
                    }
                }

                requiredRolesArray.push({
                    roleNum: count++,
                    orgRoleId: boardSlot.requiredRoleId,
                    orgRoleName: boardSlot.requiredRoleName,
                    personOrgRoleDurationId: personOrgRoleDurationId,
                    name: name,
                    met: found
                });
                boardSlotCnt++;
            });

            setRequiredRolesFormattedState({
                ...requiredRolesFormattedState,
                isArrayLoading: false,
                isArrayLoaded: true,
                arrayData: requiredRolesArray
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usasClubStaffState, requiredRolesState])

    useEffect(() => {
        if (
            clubRegistrationState.isObjLoaded === true &&
            requiredRolesState.isArrayLoaded === false &&
            requiredRolesState.isArrayLoading === false) {
            //getRequiredRolesByOrgLevelTypeId(clubRegistrationState.objData.clubOwnerType.orgLevelTypeId);
            getRequiredRolesByOrgLevelTypeIdAndOrgUnitId(clubRegistrationState.objData.clubOwnerType.orgLevelTypeId, clubRegistrationState.objData.orgUnitId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubRegistrationState, requiredRolesState])

    useEffect(() => {
        //Get club staff after a successful delete
        if ((usasClubStaffState.isSaved === true && usasClubStaffState.isSaving === false) && state.tryGetAfterDelete === true) {
            getUsasClubStaff(clubRegistrationState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
            setState({ ...state, showGrid: true, tryGetAfterDelete: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usasClubStaffState.isSaved, state.tryGetAfterDelete])

    function getInitialState() {
        return {
            isIntialPageLoadComplete: false,
            submitButton: '',
            tryRedirect: false,
            showGrid: false,
            tryGetAfterAdd: false,
            tryGetAfterDelete: false
        }
    }

    function getInitialModalState() {
        return {
            displayPopUp: false,
            modalTitle: ClubRegistrationConstants.MISSING_REQUIRED_INFORMATION
        }
    }

    return {
        clubRegistrationNavState,
        clubRegistrationState,
        usasClubStaffState,
        requiredRolesState,
        requiredRolesFormattedState,
        orgLevelTypeHelpTextState,
        registrationRoles,
        onContinueClicked,
        onSaveAndExitClicked,
        onBackClicked,
        onEdit,
        onDelete
    };
}

export default useNewClubRegistrationStaff;